/** Minimal envelope every synced document type carries. Concrete document
 *  types (StoredDiagram, StoredPattern, ...) extend this with their payload. */
export type StoredDocEnvelope = {
	id: string;
	name: string;
	createdAt: number;
	updatedAt: number;
};

/** Thrown by a DocStore implementation when the underlying storage
 *  itself is unreachable (e.g. IndexedDB fails to open) — distinct from a
 *  per-operation failure. Callers should degrade to a "storage unavailable"
 *  UI state rather than treating it as a load/save failure. Defined at the
 *  interface level so future adapters (HTTP-backed, etc.) can reuse it. */
export class DocStoreUnavailableError extends Error {
	constructor(message?: string, options?: ErrorOptions) {
		super(message ?? 'Document store is unavailable', options);
		this.name = 'DocStoreUnavailableError';
	}
}

/** Thrown on a PUT that lost a revision race — another device/tab saved this
 *  document first. `currentRevision` is the server's authoritative revision
 *  (null if the response body didn't include one); callers should reload the
 *  remote copy and reconcile rather than blindly retrying the same write. */
export class DocConflictError extends Error {
	constructor(readonly currentRevision: number | null) {
		super('document revision conflict');
		this.name = 'DocConflictError';
	}
}

/** A 409 whose body says the document now syncs through the CRDT room
 *  (`sync: 'yjs'`, SP-52): whole-document PUTs are refused for good. A
 *  subclass of DocConflictError so every existing conflict path (autosave's
 *  'conflict' status) keeps working for stale legacy tabs. */
export class DocConvertedError extends DocConflictError {
	constructor(currentRevision: number | null) {
		super(currentRevision);
		this.name = 'DocConvertedError';
	}
}

/** Thrown when the server refuses a Yjs-only call because the document is NOT
 *  converted (`409 {sync: 'legacy'}`, SP-52) — for a session that holds
 *  converted state, the sign of an admin rollback. */
export class DocNotConvertedError extends Error {
	constructor() {
		super('document is not converted to Yjs');
		this.name = 'DocNotConvertedError';
	}
}

/** How a document syncs server-side (SP-52): the legacy revision/PUT path, or
 *  the Yjs room. */
export type DocSyncMode = 'yjs' | 'legacy';

/** Thrown when the server reports a document is GONE (410) — deleted, here or
 *  on another device, permanently. Distinct from a 404 ("never existed") and
 *  emphatically NOT a RemoteUnreachableError: the server answered, and its
 *  answer is final. A tombstoned id is dead forever — never retry the write;
 *  `keepAsCopy` mints a new id instead.
 *
 *  `localDoc` is attached by SyncedDocStore when the local copy has unsynced
 *  edits worth rescuing; it is null when the throw comes straight off the wire. */
export class DocDeletedError extends Error {
	constructor(
		readonly deletedAt: string | null,
		readonly localDoc: StoredDocEnvelope | null = null,
	) {
		super('document was deleted');
		this.name = 'DocDeletedError';
	}
}

/** Thrown by RemoteDocStore.put when the serialized request body exceeds the
 *  resource's configured cap (client-side mirror of the server's 413). The
 *  local write has already succeeded by the time SyncedDocStore surfaces
 *  this — callers should show a "saved locally, too large to sync" state,
 *  not a save failure. */
export class DocTooLargeError extends Error {
	constructor(message?: string, options?: ErrorOptions) {
		super(message ?? 'document too large to sync', options);
		this.name = 'DocTooLargeError';
	}
}

/** Thrown when the server refuses to add an active document because the
 *  account is at its plan's active-document limit (`402
 *  {error: 'diagram_limit_reached', limit}`). `limit` is the server's number —
 *  never assume it. The refusal is final for that request but NOT a reason to
 *  touch local data: the device copy stays. Distinct from RemoteUnreachableError
 *  — the server answered. */
export class DocLimitError extends Error {
	constructor(readonly limit: number) {
		super(`active document limit reached (${limit})`);
		this.name = 'DocLimitError';
	}
}

/** Thrown when the server refuses a write because the document is archived
 *  (`423 {error: 'diagram_archived'}`) — an update, or a create for an id that
 *  already exists archived. The document is frozen, not gone: the local copy
 *  stays and the document can be unarchived. NOT a RemoteUnreachableError. */
export class DocArchivedError extends Error {
	constructor(message?: string, options?: ErrorOptions) {
		super(message ?? 'document is archived', options);
		this.name = 'DocArchivedError';
	}
}

/** Thrown when the server refuses a write that needs a Pro plan (`402
 *  {error: 'pro_required'}`). NOT a RemoteUnreachableError. */
export class ProRequiredError extends Error {
	constructor(message?: string, options?: ErrorOptions) {
		super(message ?? 'a Pro plan is required', options);
		this.name = 'ProRequiredError';
	}
}

/** Thrown when the remote document API can't be reached at all — offline,
 *  network failure, missing/expired auth token, or a 401/403 response.
 *  Distinct from DocConflictError: there's nothing to reconcile, callers
 *  should just degrade to the local store rather than surfacing a hard error. */
export class RemoteUnreachableError extends Error {
	constructor(message?: string, options?: ErrorOptions) {
		super(message ?? 'Remote document store is unreachable', options);
		this.name = 'RemoteUnreachableError';
	}
}

/** One gallery-list entry. TMetaExtra carries per-document-type metadata
 *  (e.g. a diagram's `kind`); pattern documents use `Record<never, never>`. */
export type DocMeta<TMetaExtra> = {
	id: string;
	name: string;
	createdAt: number;
	updatedAt: number;
	thumbnail: Blob | null;
	/** Set by SyncedDocStore.list() when the server has tombstoned this id but
	 *  the local copy holds unsynced edits. Clean copies are purged instead, so
	 *  this flag ALWAYS means "there is real work here to keep or discard".
	 *  Never set by the local-only store. */
	deletedElsewhere?: boolean;
	/** Set by SyncedDocStore.list() for ids it is about to upload — documents
	 *  this account's server has never seen, so they exist on this device only
	 *  until the upload lands (and may be refused: plan limit). Never set by the
	 *  server or the local-only store. */
	localOnly?: boolean;
} & TMetaExtra;

/** Async by design: phase 2 swaps in an HTTP-backed implementation without
 *  touching any consumer. `load` returns null for unknown ids and THROWS on
 *  corrupt/unparseable documents — callers must not overwrite on throw. */
export interface DocStore<TDoc extends StoredDocEnvelope, TMetaExtra> {
	list(): Promise<DocMeta<TMetaExtra>[]>;
	load(id: string): Promise<TDoc | null>;
	save(doc: TDoc): Promise<void>;
	saveThumbnail(id: string, blob: Blob): Promise<void>;
	delete(id: string): Promise<void>;
}

/** What we last successfully synced for a document: the server revision (the
 *  optimistic-lock base for PUTs) and the document's `updatedAt` AT THAT MOMENT.
 *  The second field is what lets us tell an in-sync local copy from one with
 *  unsynced edits — an offline save advances `updatedAt` but never the revision,
 *  so without it "in sync" and "edited offline" are indistinguishable.
 *  `syncedUpdatedAt` is null for records written before it existed; those read
 *  as CLEAN. */
export type SyncMeta = { revision: number; syncedUpdatedAt: number | null };

/** Tracks the last server revision seen per document — the optimistic-lock
 *  base for PUTs. `getRevision` returns null when no revision has been
 *  recorded for the id (never synced, or cleared by `deleteRevision`).
 *  `getSyncMeta` returns the fuller record (revision + syncedUpdatedAt) that
 *  SyncedDocStore uses to tell a clean, already-synced local copy from a
 *  dirty one — also null whenever no revision has been recorded, which by
 *  convention means "no evidence this doc was ever synced" and is handled
 *  as the dirty case, never purged. */
export interface SyncRevisionStore {
	getRevision(id: string): Promise<number | null>;
	getSyncMeta(id: string): Promise<SyncMeta | null>;
	setRevision(id: string, revision: number, syncedUpdatedAt: number): Promise<void>;
	deleteRevision(id: string): Promise<void>;
	/** Every locally-cached thumbnail, keyed by document id. Distinct from
	 *  `list()`, which only surfaces thumbnails joined onto a LOCAL document —
	 *  the sync layer needs thumbnails keyed independently so `reconcileDocLists`
	 *  can paint a preview for a document that lives only on the remote (one this
	 *  device has never opened, so it has no local document record, yet whose
	 *  thumbnail `backfillThumbnails` has cached). Without this, such previews
	 *  stay blank until the diagram is opened, which is what mints its local
	 *  record. */
	getThumbnails(): Promise<Map<string, Blob>>;
}

/** Local persistence of a document's merged Yjs state (SP-52 A1). Implemented
 *  by the IndexedDB store when `yDocs` is enabled and by in-memory fakes. */
export interface YDocPersistence {
	/** Whether the Yjs methods work. Every IndexedDbDocStore has them, but they
	 *  throw unless its `yDocs` store exists (e.g. azabu-patterns has none), so
	 *  callers must check this rather than the methods being present. */
	readonly hasYDocs: boolean;
	loadYDoc(id: string): Promise<Uint8Array | null>;
	saveYDoc(id: string, update: Uint8Array): Promise<void>;
	deleteYDoc(id: string): Promise<void>;
}
